import { Box, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { FaCheck, FaExclamationTriangle, FaTimes } from 'react-icons/fa'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import { TextoTruncado } from '../../../../../components/TextoTruncado'
import { CriterioResponse, DescricaoCargo, DescritivoReajusteRequestPut, iCriteriosPontuacao, iSolicitacaoReajuste } from '../../../../../interfaces'
import { formatarDataHora } from '../../../utils/data'
import {
  CampoAjuste,
  consolidarPontos,
  decidirPonto,
  DecisaoPonto,
  iGrupoPontos,
  iPontoSolicitado,
  MapaDecisoes,
  montarPayloadsDecisao,
  normalizarNivel,
  pontosDoBloco,
  tituloCampo
} from '../../../utils/consolidacao'

interface iProps {
  isOpen: boolean
  onClose: () => void
  cargo?: DescricaoCargo
  criterios: CriterioResponse[]
  solicitacoes: iSolicitacaoReajuste[]
  nomePorEmail: (email: string) => string
  campoInicial?: CampoAjuste
  onAplicar: (payloads: DescritivoReajusteRequestPut[]) => Promise<void>
}

// Pra cada criterio que de fato mudou entre o atual e o solicitado, monta o par de linhas
// (atual/solicitado) - sem isso so dava pra saber "quantos" mudaram, numa caixa genérica
function descreverCriteriosDiferentes (
  nivel: string | undefined,
  cargo: DescricaoCargo | undefined,
  criterios: CriterioResponse[],
  criteriosSolicitados: iCriteriosPontuacao[]
): Array<{ atual: string, solicitado: string }> {
  const chaveNivel = Object.keys(cargo?.niveis ?? {}).find(chave => normalizarNivel(chave) === normalizarNivel(nivel))
  const atuais = (chaveNivel !== undefined ? cargo?.niveis[chaveNivel]?.criteriosPontuacao : undefined) ?? []

  const rotuloOpcao = (criterioId: string, minimo: boolean, opcaoId?: string): string => {
    if (opcaoId === undefined || opcaoId === '') return 'Nenhuma opção selecionada'

    const criterio = criterios.find(c => c.id === criterioId)
    const opcao = (minimo ? criterio?.niveisMinimos : criterio?.niveis)?.find(n => n.id === opcaoId)

    return opcao !== undefined ? `${opcao.pontuacao} - ${opcao.nome}` : opcaoId
  }

  const chaveDoCriterio = (c: iCriteriosPontuacao): string => `${c.criterioId}|${c.minimo === true ? '1' : '0'}`
  const chaves = new Set([...atuais.map(chaveDoCriterio), ...criteriosSolicitados.map(chaveDoCriterio)])

  const linhas: Array<{ atual: string, solicitado: string }> = []

  chaves.forEach((chaveComposta) => {
    const [criterioId, minimoStr] = chaveComposta.split('|')
    const minimo = minimoStr === '1'
    const atual = atuais.find(c => c.criterioId === criterioId && (c.minimo === true) === minimo)
    const solicitado = criteriosSolicitados.find(c => c.criterioId === criterioId && (c.minimo === true) === minimo)

    if ((atual?.opcaoPontuacaoId ?? '') === (solicitado?.opcaoPontuacaoId ?? '')) return

    const criterio = criterios.find(c => c.id === criterioId)
    const nomeCriterio = criterio !== undefined ? `${criterio.nomeCriterio}${minimo ? ' - Mínimo' : ''}` : 'Critério'

    linhas.push({
      atual: `${nomeCriterio}: ${rotuloOpcao(criterioId, minimo, atual?.opcaoPontuacaoId)}`,
      solicitado: `${nomeCriterio}: ${rotuloOpcao(criterioId, minimo, solicitado?.opcaoPontuacaoId)}`
    })
  })

  return linhas
}

const rotuloOperacao: Record<iPontoSolicitado['operacao'], string> = {
  inclusao: 'Inclusão',
  remocao: 'Exclusão',
  alteracao: 'Alteração'
}

const corOperacao: Record<iPontoSolicitado['operacao'], string> = {
  inclusao: 'var(--Azul)',
  remocao: 'var(--Red)',
  alteracao: 'var(--Roxo)'
}

const rotuloDecisao: Record<DecisaoPonto, string> = {
  aceito: 'Aceito',
  recusado: 'Recusado'
}

function Etiqueta ({ texto, cor, fundo }: { texto: string, cor: string, fundo: string }): JSX.Element {
  return (
    <Text
      fontSize={'11px'}
      fontWeight={'700'}
      textTransform={'uppercase'}
      letterSpacing={'.03em'}
      color={cor}
      bgColor={fundo}
      borderRadius={'.75rem'}
      px={'.5rem'}
      py={'.125rem'}
      whiteSpace={'nowrap'}
    >
      {texto}
    </Text>
  )
}

function ValorDoPonto ({ rotulo, texto, cor, fundo, borda }: { rotulo: string, texto: string, cor: string, fundo: string, borda: string }): JSX.Element {
  return (
    <Box flex={'1'} minW={'0'}>
      <Text fontSize={'11px'} color={'var(--Gray3)'} mb={'.1875rem'}>{rotulo}</Text>
      <Box bgColor={fundo} borderLeft={'3px solid'} borderColor={borda} borderRadius={'.25rem'} p={'.5rem .625rem'}>
        <TextoTruncado texto={texto} linhas={4} fontSize={'13px'} color={cor} whiteSpace={'pre-wrap'} />
      </Box>
    </Box>
  )
}

function CartaoPonto ({ ponto, grupo, decisoes, onDecidir, cargo, criterios }: {
  ponto: iPontoSolicitado
  grupo: iGrupoPontos
  decisoes: MapaDecisoes
  onDecidir: (decisao: DecisaoPonto) => void
  cargo?: DescricaoCargo
  criterios: CriterioResponse[]
}): JSX.Element {
  const decisao = decisoes[ponto.chave]

  // Aceitar uma versao do ponto em conflito trava as concorrentes, em vez de sobrescrever em silencio
  const bloqueadoPorConflito = grupo.temConflito &&
    decisao !== 'aceito' &&
    grupo.pontos.some(outro => outro.chave !== ponto.chave && decisoes[outro.chave] === 'aceito')

  return (
    <Box
      border={'1px solid'}
      borderColor={decisao === 'aceito' ? 'var(--Green2)' : decisao === 'recusado' ? 'var(--Red)' : 'var(--Gray5)'}
      borderRadius={'.375rem'}
      bgColor={'white'}
      p={'.875rem'}
      opacity={bloqueadoPorConflito ? 0.6 : 1}
    >
      <Flex justifyContent={'space-between'} alignItems={'flex-start'} gap={'.75rem'} flexWrap={'wrap'} mb={'.625rem'}>
        <Flex direction={'column'} gap={'.25rem'} minW={'0'} flex={'1'}>
          {/* Cargo superior e proximo cargo da trilha usam nomes de cargo parecidos: sem repetir
          a categoria em cada cartao, fica facil decidir um ponto sem notar em qual campo ele cai */}
          <Text fontSize={'11px'} color={'var(--Gray3)'} fontWeight={'600'} textTransform={'uppercase'}>
            {tituloCampo[ponto.campo]}
          </Text>
          <Flex gap={'.375rem'} alignItems={'center'} flexWrap={'wrap'}>
            <Etiqueta
              texto={rotuloOperacao[ponto.operacao]}
              cor={corOperacao[ponto.operacao]}
              fundo={ponto.operacao === 'inclusao' ? 'var(--AzulOpacoBg)' : ponto.operacao === 'remocao' ? 'var(--RedOpacoBg)' : 'var(--Gray6)'}
            />
            {ponto.nivel !== undefined && ponto.nivel !== '' && (
              <Text fontSize={'12px'} color={'var(--Gray3)'}>{ponto.nivel}</Text>
            )}
            {ponto.posicao !== undefined && (
              <Text fontSize={'12px'} color={'var(--Gray3)'}>· ponto {ponto.posicao}</Text>
            )}
          </Flex>

          {/* Quem pediu fica colado no ponto: com varios solicitantes na mesma tela o nome no topo nao resolve */}
          <Flex gap={'.25rem'} alignItems={'center'} flexWrap={'wrap'}>
            <Text fontSize={'12px'} color={'var(--Gray2)'} fontWeight={'600'}>{ponto.nomeSolicitante}</Text>
            <Text fontSize={'11px'} color={'var(--Gray3)'}>({ponto.emailSolicitante})</Text>
            {formatarDataHora(ponto.dataSolicitacao) !== undefined && (
              <Text fontSize={'11px'} color={'var(--Gray3)'}>· {formatarDataHora(ponto.dataSolicitacao)}</Text>
            )}
          </Flex>
        </Flex>

        <Flex gap={'.375rem'} alignItems={'center'}>
          {decisao !== undefined && (
            <Etiqueta
              texto={rotuloDecisao[decisao]}
              cor={decisao === 'aceito' ? 'var(--Green1)' : 'var(--Red)'}
              fundo={decisao === 'aceito' ? 'var(--Green2OpacoBg)' : 'var(--RedOpacoBg)'}
            />
          )}
          <ButtonCmp
            small
            leftIcon={<FaCheck size={11} />}
            VarColor={decisao === 'aceito' ? 'Green2' : undefined}
            OutlineColor={decisao === 'aceito' ? undefined : 'Green2'}
            isDisabled={bloqueadoPorConflito}
            onClick={() => onDecidir('aceito')}
          >
            Aceitar
          </ButtonCmp>
          <ButtonCmp
            small
            leftIcon={<FaTimes size={11} />}
            VarColor={decisao === 'recusado' ? 'Red' : undefined}
            OutlineColor={decisao === 'recusado' ? undefined : 'Red'}
            isDisabled={bloqueadoPorConflito}
            onClick={() => onDecidir('recusado')}
          >
            Recusar
          </ButtonCmp>
        </Flex>
      </Flex>

      <Flex gap={'.75rem'} flexDir={{ base: 'column', md: 'row' }}>
        {ponto.valorAnterior !== undefined && ponto.valorAnterior !== '' && (
          <ValorDoPonto
            rotulo={ponto.operacao === 'remocao' ? 'Conteúdo que pediram para remover' : 'Conteúdo atual'}
            texto={ponto.valorAnterior}
            cor={'var(--Gray1)'}
            fundo={'var(--RedOpacoBg)'}
            borda={'var(--Red)'}
          />
        )}
        {ponto.valorSolicitado !== undefined && ponto.valorSolicitado !== '' && (
          <ValorDoPonto
            rotulo={ponto.operacao === 'inclusao' ? 'Conteúdo que pediram para incluir' : 'Conteúdo solicitado'}
            texto={ponto.valorSolicitado}
            cor={'var(--Gray1)'}
            fundo={'var(--AzulOpacoBg)'}
            borda={'var(--Azul)'}
          />
        )}
        {ponto.criteriosSolicitados !== undefined && (() => {
          const diferencas = descreverCriteriosDiferentes(ponto.nivel, cargo, criterios, ponto.criteriosSolicitados)
          const textoAtual = diferencas.map(d => d.atual).join('\n')
          const textoSolicitado = diferencas.map(d => d.solicitado).join('\n')

          return (
            <>
              {textoAtual !== '' && (
                <ValorDoPonto
                  rotulo={'Pontuação atual'}
                  texto={textoAtual}
                  cor={'var(--Gray1)'}
                  fundo={'var(--RedOpacoBg)'}
                  borda={'var(--Red)'}
                />
              )}
              {textoSolicitado !== '' && (
                <ValorDoPonto
                  rotulo={'Pontuação solicitada'}
                  texto={textoSolicitado}
                  cor={'var(--Gray1)'}
                  fundo={'var(--AzulOpacoBg)'}
                  borda={'var(--Azul)'}
                />
              )}
            </>
          )
        })()}
      </Flex>

      {ponto.motivo !== undefined && ponto.motivo !== '' && (
        <Box mt={'.625rem'}>
          <Text fontSize={'11px'} color={'var(--Gray3)'} mb={'.125rem'}>Motivo informado</Text>
          <TextoTruncado texto={ponto.motivo} linhas={3} fontSize={'13px'} color={'var(--Gray2)'} whiteSpace={'pre-wrap'} />
        </Box>
      )}
    </Box>
  )
}

export const ModalAnaliseConsolidada: React.FC<iProps> = ({ isOpen, onClose, cargo, criterios, solicitacoes, nomePorEmail, campoInicial, onAplicar }) => {
  const [decisoes, setDecisoes] = useState<MapaDecisoes>({})
  const [campoFiltro, setCampoFiltro] = useState<CampoAjuste | undefined>(campoInicial)
  const [aplicando, setAplicando] = useState(false)
  const [erro, setErro] = useState<string>()

  const blocos = useMemo(
    () => consolidarPontos(cargo, solicitacoes, nomePorEmail),
    [cargo, solicitacoes, nomePorEmail]
  )

  useEffect(() => {
    if (isOpen) {
      setCampoFiltro(campoInicial)
      setErro(undefined)
    }
  }, [isOpen, campoInicial])

  const blocosVisiveis = campoFiltro !== undefined
    ? blocos.filter(bloco => bloco.campo === campoFiltro)
    : blocos

  const todosPontos = blocos.flatMap(pontosDoBloco)
  const decididos = todosPontos.filter(ponto => decisoes[ponto.chave] !== undefined).length

  async function aplicar (): Promise<void> {
    if (!cargo) return

    setAplicando(true)
    setErro(undefined)

    try {
      await onAplicar(montarPayloadsDecisao(cargo, blocos, decisoes, solicitacoes))
      setDecisoes({})
      onClose()
    } catch (e) {
      setErro('Não foi possível registrar as decisões. Tente novamente.')
    } finally {
      setAplicando(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={'6xl'} scrollBehavior={'inside'}>
      <ModalOverlay />
      <ModalContent maxW={'1100px'}>
        <ModalHeader borderBottom={'1px solid var(--Gray5)'} pb={'.75rem'}>
          <Text fontSize={'18px'} fontWeight={'600'} color={'var(--Azul)'}>Análise das solicitações de ajuste</Text>
          <Text fontSize={'12px'} color={'var(--Gray3)'} fontWeight={'400'} mt={'.25rem'}>
            {solicitacoes.length === 1
              ? '1 solicitação pendente · decida cada ponto individualmente'
              : `${solicitacoes.length} solicitações pendentes · decida cada ponto individualmente`}
          </Text>

          {blocos.length > 1 && (
            <Flex gap={'.375rem'} mt={'.75rem'} flexWrap={'wrap'}>
              <ButtonCmp
                small
                VarColor={campoFiltro === undefined ? 'Azul' : undefined}
                OutlineColor={campoFiltro === undefined ? undefined : 'c6'}
                onClick={() => setCampoFiltro(undefined)}
              >
                Todas ({todosPontos.length})
              </ButtonCmp>
              {blocos.map(bloco => (
                <ButtonCmp
                  key={bloco.campo}
                  small
                  VarColor={campoFiltro === bloco.campo ? 'Azul' : undefined}
                  OutlineColor={campoFiltro === bloco.campo ? undefined : 'c6'}
                  onClick={() => setCampoFiltro(bloco.campo)}
                >
                  {bloco.titulo} ({pontosDoBloco(bloco).length})
                </ButtonCmp>
              ))}
            </Flex>
          )}
        </ModalHeader>
        <ModalCloseButton color={'var(--Gray2)'} bgColor={'transparent'} />

        <ModalBody py={'1rem'} bgColor={'var(--Gray6)'}>
          {blocosVisiveis.length === 0
            ? <Text fontSize={'14px'} color={'var(--Gray2)'}>Nenhum ponto pendente de análise.</Text>
            : (
              <Flex direction={'column'} gap={'1.25rem'}>
                {blocosVisiveis.map(bloco => (
                  <Box key={bloco.campo}>
                    <Text fontSize={'15px'} fontWeight={'700'} color={'var(--Gray1)'} mb={'.625rem'}>
                      {bloco.titulo}
                    </Text>

                    <Flex direction={'column'} gap={'.75rem'}>
                      {bloco.grupos.map((grupo) => {
                        return (
                          <Box
                            key={grupo.chaveAlvo}
                            border={grupo.temConflito ? '1px solid var(--Orange)' : undefined}
                            borderRadius={grupo.temConflito ? '.375rem' : undefined}
                            bgColor={grupo.temConflito ? 'var(--OrangeOpacoBg)' : undefined}
                            p={grupo.temConflito ? '.625rem' : undefined}
                          >
                            {grupo.temConflito && (
                              <Flex gap={'.375rem'} alignItems={'center'} mb={'.5rem'} color={'var(--OrangeEscuro)'}>
                                <FaExclamationTriangle size={13} />
                                <Text fontSize={'12px'} fontWeight={'600'}>
                                  Solicitantes diferentes pediram coisas diferentes para o mesmo ponto: apenas uma versão pode ser aceita.
                                </Text>
                              </Flex>
                            )}

                            <Flex direction={'column'} gap={'.5rem'}>
                              {grupo.pontos.map(ponto => (
                                <CartaoPonto
                                  key={ponto.chave}
                                  ponto={ponto}
                                  grupo={grupo}
                                  decisoes={decisoes}
                                  cargo={cargo}
                                  criterios={criterios}
                                  onDecidir={decisao => setDecisoes(decidirPonto(decisoes, grupo, ponto, decisao))}
                                />
                              ))}
                            </Flex>
                          </Box>
                        )
                      })}
                    </Flex>
                  </Box>
                ))}
              </Flex>
              )}
        </ModalBody>

        <ModalFooter borderTop={'1px solid var(--Gray5)'} justifyContent={'space-between'} gap={'.75rem'}>
          <Flex direction={'column'}>
            <Text fontSize={'13px'} color={'var(--Gray2)'}>
              {decididos} de {todosPontos.length} {todosPontos.length === 1 ? 'ponto decidido' : 'pontos decididos'}
            </Text>
            {erro !== undefined && <Text fontSize={'12px'} color={'var(--Red)'}>{erro}</Text>}
          </Flex>

          <Flex gap={'.5rem'}>
            <ButtonCmp OutlineColor={'c6'} onClick={onClose}>Fechar</ButtonCmp>
            <ButtonCmp
              VarColor={'Green2'}
              isDisabled={decididos === 0}
              isLoading={aplicando}
              loadingText={'Registrando...'}
              onClick={aplicar}
            >
              Registrar decisões
            </ButtonCmp>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
